import type { BoardDto } from '@mm/shared';
import { useState } from 'react';
import { markBoard } from '../../api/assignments';
import { getBoard } from '../../api/boards';
import { CelebrationOverlay } from '../../components/CelebrationOverlay';
import { fireCelebration, pickFinishPhrase } from '../../lib/celebrate';
import { cn } from '../../lib/cn';
import { Button } from '../../ui/Button';
import { formatDue, formatTimeSpent, STATUS_LABEL, videoEmbedUrl } from './board-ui';

export function StudentAssignmentPanel({
  board,
  onChange,
}: {
  board: BoardDto;
  onChange: (b: BoardDto) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [celebration, setCelebration] = useState<string | null>(null);
  const due = formatDue(board.dueAt);
  const time = formatTimeSpent(board.timeSpentSec);
  const embed = board.hintVideoUrl ? videoEmbedUrl(board.hintVideoUrl) : null;
  const hasHint = !!(board.hintText || board.hintVideoUrl);

  const canFinish =
    board.status === 'NEW' || board.status === 'VIEWED' || board.status === 'WORKING';
  const canResume = board.status === 'FINISHED' || board.status === 'REVIEWED';

  async function mark(m: 'finished' | 'working') {
    setBusy(true);
    try {
      await markBoard(board.id, m);
      const fresh = await getBoard(board.id);
      onChange(fresh);
      if (m === 'finished') {
        fireCelebration();
        setCelebration(pickFinishPhrase());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="w-[320px] shrink-0 border-l border-border bg-panel overflow-y-auto flex flex-col">
      <div className="px-5 py-4 border-b border-border">
        <div className="font-caps italic text-[11px] uppercase tracking-[0.12em] text-muted mb-1">
          Задание
        </div>
        <div className="flex items-center gap-2 mb-2">
          <StudentStatusPill status={board.status} />
          {due ? <span className="text-[12px] text-muted">до {due}</span> : null}
        </div>
        {time ? (
          <div className="text-[12px] text-muted">⌛ вы работали {time}</div>
        ) : null}

        <div className="mt-3 space-y-2">
          {canFinish ? (
            <Button
              className="w-full"
              onClick={() => void mark('finished')}
              disabled={busy}
            >
              Я закончил(а)
            </Button>
          ) : null}
          {canResume ? (
            <>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => void mark('working')}
                disabled={busy}
              >
                Хочу продолжить
              </Button>
              <p className="text-[11px] text-muted leading-snug">
                Снимет задание с проверки и вернёт его в работу.
              </p>
            </>
          ) : null}
        </div>
      </div>

      {board.tutorComment ? (
        <div className="px-5 py-4 border-b border-border">
          <div className="font-caps italic text-[11px] uppercase tracking-[0.12em] text-muted mb-1">
            От репетитора
          </div>
          <p className="text-[13px] whitespace-pre-wrap">{board.tutorComment}</p>
        </div>
      ) : null}

      {hasHint ? (
        <div className="px-5 py-4 flex-1">
          <div className="font-caps italic text-[11px] uppercase tracking-[0.12em] text-muted mb-2">
            💡 Подсказка
          </div>
          {board.hintText ? (
            <p className="text-[13px] whitespace-pre-wrap mb-3">{board.hintText}</p>
          ) : null}
          {embed ? (
            <div className="aspect-video w-full rounded overflow-hidden border border-border bg-bg">
              <iframe
                src={embed}
                title="Видео-подсказка"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : board.hintVideoUrl ? (
            <a
              href={board.hintVideoUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[13px] text-link underline"
            >
              Открыть видео
            </a>
          ) : null}
        </div>
      ) : null}

      {celebration ? (
        <CelebrationOverlay phrase={celebration} onClose={() => setCelebration(null)} />
      ) : null}
    </aside>
  );
}

function StudentStatusPill({ status }: { status: BoardDto['status'] }) {
  const tone =
    status === 'REVIEWED'
      ? 'text-accent border-accent/40 bg-accent-soft'
      : status === 'FINISHED'
        ? 'text-fg border-fg/40'
        : status === 'WORKING'
          ? 'text-fg border-border'
          : 'text-muted border-border-soft';
  return (
    <span
      className={cn(
        'inline-block font-caps italic text-[11px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border',
        tone,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}
